import { useState } from 'react';
import { 
  HelpCircle, 
  BookOpen, 
  PlayCircle, 
  MessageCircle, 
  ChevronDown,
  ShoppingCart,
  Grid3X3,
  Receipt,
  Landmark,
  Boxes,
  BarChart3,
  Users,
  Printer,
  Settings,
  Smartphone,
  Search,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { FileKey, Server as ServerIcon, Terminal } from 'lucide-react';

const faqCategories = [
  {
    id: 'fiscal',
    title: 'Fiscal / NFC-e',
    icon: Receipt,
    questions: [
      {
        q: 'Como funciona a emissão de NFC-e no Altus Food?',
        a: 'A emissão de NFC-e é feita através do ACBrMonitorPlus instalado no computador do restaurante, conectado ao certificado digital A1. O sistema se comunica de forma segura com o ACBr local para processar as notas fiscais.',
      },
      {
        q: 'Preciso de algum software adicional para emitir NFC-e?',
        a: 'Sim, é necessário ter o ACBrMonitorPlus instalado e configurado no Windows local, com o certificado digital A1 válido. A configuração inicial é feita pelo suporte técnico.',
      },
      {
        q: 'Como ativar a emissão de NFC-e no meu restaurante?',
        a: 'Entre em contato com o suporte técnico para ativar a emissão fiscal. Nossa equipe irá configurar todo o ambiente necessário no seu computador.',
      },
      {
        q: 'A emissão de notas parou de funcionar. O que fazer?',
        a: 'Verifique se o ACBrMonitor está rodando no computador (ícone na bandeja do sistema). Se o problema persistir, entre em contato com o suporte técnico para diagnóstico.',
      },
      {
        q: 'Posso emitir NFC-e de qualquer computador?',
        a: 'A emissão precisa ser feita a partir do computador onde o ACBrMonitor está instalado e configurado com o certificado digital. Entre em contato com o suporte para configurar novos pontos de emissão.',
      },
    ],
  },
  {
    id: 'pdv',
    title: 'PDV e Vendas',
    icon: ShoppingCart,
    questions: [
      {
        q: 'Como abrir uma nova venda?',
        a: 'Na tela do PDV, selecione o tipo de atendimento (Mesa, Comanda ou Balcão). Para Mesa, escolha a mesa desejada. Para Comanda, insira o número da comanda. Para Balcão, a venda é direta sem vinculação.',
      },
      {
        q: 'Como adicionar produtos ao pedido?',
        a: 'Após selecionar o tipo de atendimento, navegue pelas categorias e clique nos produtos para adicionar. Use o campo de busca para encontrar produtos rapidamente. Você pode ajustar a quantidade e adicionar observações.',
      },
      {
        q: 'Como aplicar desconto em uma venda?',
        a: 'No checkout, você pode aplicar desconto informando o valor ou percentual. O desconto será calculado automaticamente sobre o subtotal.',
      },
      {
        q: 'Como cancelar um item já enviado?',
        a: 'Itens já enviados para produção aparecem com um indicador. Para cancelar, clique no item e selecione "Cancelar Item". Informe o motivo do cancelamento para registro.',
      },
      {
        q: 'Como dividir a conta entre clientes?',
        a: 'No momento do checkout, você pode dividir o valor total pelo número de pessoas. O sistema calculará o valor individual automaticamente.',
      },
    ],
  },
  {
    id: 'mesas',
    title: 'Mesas e Comandas',
    icon: Grid3X3,
    questions: [
      {
        q: 'Como cadastrar novas mesas?',
        a: 'Acesse o menu Mesas e clique em "Nova Mesa". Você pode cadastrar mesas individualmente ou em lote, definindo capacidade e nome/apelido.',
      },
      {
        q: 'Como transferir itens entre mesas?',
        a: 'Na visualização da mesa de origem, selecione os itens que deseja transferir e clique em "Transferir". Escolha a mesa de destino e confirme.',
      },
      {
        q: 'O que significam as cores das mesas?',
        a: 'Verde: mesa livre. Amarelo: mesa ocupada. Vermelho: mesa com chamada de garçom. As cores ajudam a visualizar rapidamente o status do salão.',
      },
      {
        q: 'Como fechar uma mesa/comanda?',
        a: 'No PDV, selecione a mesa/comanda ocupada e clique em "Fechar Conta". Escolha a forma de pagamento (pode ser múltiplas), aplique taxa de serviço se habilitada, e finalize.',
      },
    ],
  },
  {
    id: 'caixa',
    title: 'Caixa',
    icon: Landmark,
    questions: [
      {
        q: 'Como abrir o caixa?',
        a: 'Acesse a página Caixa e clique em "Abrir Caixa". Informe o valor inicial em dinheiro (fundo de troco). O sistema registrará o horário e o responsável pela abertura.',
      },
      {
        q: 'Como fazer sangria ou suprimento?',
        a: 'Com o caixa aberto, use os botões "Sangria" (retirada) ou "Suprimento" (entrada). Informe o valor e motivo da movimentação.',
      },
      {
        q: 'Como fechar o caixa?',
        a: 'Clique em "Fechar Caixa" e faça a contagem de cédulas e moedas. O sistema comparará com o valor esperado e mostrará a diferença, se houver.',
      },
      {
        q: 'O que fazer se houver diferença no caixa?',
        a: 'Registre observações explicando a diferença. O sistema mantém histórico completo para análise posterior. Diferenças frequentes devem ser investigadas pela gerência.',
      },
    ],
  },
  {
    id: 'estoque',
    title: 'Estoque',
    icon: Boxes,
    questions: [
      {
        q: 'Como cadastrar ingredientes?',
        a: 'Acesse Estoque e clique em "Novo Ingrediente". Informe nome, unidade de medida, custo por unidade e estoque mínimo para alerta.',
      },
      {
        q: 'Como fazer entrada de estoque?',
        a: 'Selecione o ingrediente e clique em "Entrada". Informe a quantidade e o custo unitário. O sistema usa média ponderada para calcular o novo custo.',
      },
      {
        q: 'Como vincular ingredientes aos produtos?',
        a: 'Na edição do produto, acesse a aba "Receita". Adicione os ingredientes com as quantidades utilizadas. O estoque será baixado automaticamente nas vendas.',
      },
      {
        q: 'O que fazer quando aparecer alerta de estoque baixo?',
        a: 'O alerta indica que o estoque atingiu o mínimo definido. Faça o pedido de reposição ao fornecedor e registre a entrada quando receber.',
      },
    ],
  },
  {
    id: 'relatorios',
    title: 'Relatórios',
    icon: BarChart3,
    questions: [
      {
        q: 'Como gerar relatório de vendas?',
        a: 'Acesse Relatórios, selecione o período desejado e visualize os dados. Você pode exportar em Excel para análise detalhada.',
      },
      {
        q: 'O que é o CMV?',
        a: 'CMV (Custo de Mercadoria Vendida) mostra quanto você gastou em ingredientes para cada produto vendido. É essencial para calcular sua margem de lucro real.',
      },
      {
        q: 'Como analisar o ticket médio?',
        a: 'O Ticket Médio mostra o valor médio por pedido. Acompanhe por período, horário e dia da semana para identificar oportunidades de aumento de vendas.',
      },
      {
        q: 'Como ver produtos mais vendidos?',
        a: 'No relatório de vendas, a seção "Produtos Mais Vendidos" mostra o ranking por quantidade e faturamento. Use para otimizar seu cardápio.',
      },
    ],
  },
  {
    id: 'equipe',
    title: 'Equipe e Permissões',
    icon: Users,
    questions: [
      {
        q: 'Como adicionar um novo funcionário?',
        a: 'Acesse Equipe e clique em "Convidar Membro". Informe email, nome e cargo (Gerente, Caixa ou Garçom). O funcionário receberá um email para criar a senha.',
      },
      {
        q: 'Quais são os níveis de permissão?',
        a: 'Proprietário: acesso total. Gerente: acesso ao dashboard e relatórios. Caixa: PDV, caixa e pedidos. Garçom: apenas app do garçom e PDV básico.',
      },
      {
        q: 'Como alterar o cargo de um funcionário?',
        a: 'Na lista da Equipe, clique no funcionário e selecione "Editar". Altere o cargo conforme necessário. As permissões são aplicadas imediatamente.',
      },
      {
        q: 'Como remover um funcionário?',
        a: 'Na lista da Equipe, clique no funcionário e selecione "Remover". O acesso será revogado imediatamente. Os registros de operações permanecem no sistema.',
      },
    ],
  },
  {
    id: 'impressao',
    title: 'Impressão',
    icon: Printer,
    questions: [
      {
        q: 'Como configurar a impressora?',
        a: 'Acesse Impressoras e clique em "Nova Impressora". Selecione a impressora instalada no sistema (via QZ Tray) e defina nome e funções.',
      },
      {
        q: 'O que é o QZ Tray?',
        a: 'QZ Tray é um software que permite imprimir diretamente do navegador. Baixe em qz.io, instale e mantenha executando. É necessário para impressoras térmicas.',
      },
      {
        q: 'Como configurar locais de produção?',
        a: 'Em Locais de Produção, crie locais como "Cozinha" e "Bar". Vincule cada local a uma impressora. Depois, vincule os produtos aos locais correspondentes.',
      },
      {
        q: 'A impressão não está funcionando. O que fazer?',
        a: '1) Verifique se o QZ Tray está executando (ícone na bandeja). 2) Teste a conexão na página de Impressoras. 3) Verifique se a impressora está online no Windows.',
      },
    ],
  },
  {
    id: 'garcom',
    title: 'App do Garçom',
    icon: Smartphone,
    questions: [
      {
        q: 'Como acessar o app do garçom?',
        a: 'O garçom acessa pelo menu lateral em "Garçom". A interface é otimizada para celular, permitindo registrar pedidos diretamente na mesa.',
      },
      {
        q: 'Como chamar o garçom pela mesa?',
        a: 'Os clientes podem escanear o QR Code da mesa e clicar em "Chamar Garçom". O garçom recebe notificação em tempo real.',
      },
      {
        q: 'As notificações não estão chegando. O que fazer?',
        a: 'Verifique se as notificações push estão habilitadas no navegador. No app, clique no ícone de sino e ative as notificações quando solicitado.',
      },
    ],
  },
  {
    id: 'configuracoes',
    title: 'Configurações Gerais',
    icon: Settings,
    questions: [
      {
        q: 'Como alterar a logo do restaurante?',
        a: 'Acesse Configurações e na seção "Logo", clique para fazer upload de uma nova imagem. Formatos aceitos: PNG, JPG. Tamanho recomendado: 200x200px.',
      },
      {
        q: 'Como configurar a taxa de serviço?',
        a: 'Em Configurações, habilite "Taxa de Serviço" e defina o percentual (geralmente 10%). A taxa será calculada automaticamente no checkout.',
      },
      {
        q: 'Como habilitar o couvert artístico?',
        a: 'Em Configurações, ative "Couvert" e defina o valor por pessoa. No checkout, você poderá informar a quantidade de pessoas.',
      },
      {
        q: 'Como ativar o cardápio digital?',
        a: 'Em Configurações, habilite "Cardápio Digital Público". Gere o QR Code na página de Mesas para os clientes acessarem o menu.',
      },
    ],
  },
];

const tutorials = [
  {
    id: 'first-sale',
    title: 'Sua Primeira Venda',
    description: 'Aprenda a realizar uma venda completa do início ao fim',
    duration: '5 min',
    steps: [
      'Acesse o PDV no menu lateral',
      'Selecione "Mesa" e escolha uma mesa livre',
      'Navegue pelas categorias e adicione produtos',
      'Clique em "Enviar" para confirmar o pedido',
      'Quando o cliente pedir a conta, clique na mesa',
      'Selecione "Fechar Conta" e escolha a forma de pagamento',
      'Confirme e pronto! Venda finalizada.',
    ],
  },
  {
    id: 'cash-register',
    title: 'Abertura e Fechamento de Caixa',
    description: 'Como gerenciar o caixa diário do restaurante',
    duration: '4 min',
    steps: [
      'Acesse a página "Caixa" no menu',
      'Clique em "Abrir Caixa" no início do expediente',
      'Informe o valor do fundo de troco',
      'Durante o dia, registre sangrias e suprimentos se necessário',
      'No fim do expediente, clique em "Fechar Caixa"',
      'Conte as cédulas e moedas e informe os valores',
      'Verifique a diferença e adicione observações se houver',
    ],
  },
  {
    id: 'team-setup',
    title: 'Configurando sua Equipe',
    description: 'Como adicionar e gerenciar funcionários',
    duration: '3 min',
    steps: [
      'Acesse "Equipe" no menu Sistema',
      'Clique em "Convidar Membro"',
      'Preencha email, nome e selecione o cargo',
      'O funcionário receberá um email para criar a senha',
      'Após criar a conta, ele terá acesso às funções do cargo',
    ],
  },
  {
    id: 'stock-control',
    title: 'Controle de Estoque Básico',
    description: 'Cadastre ingredientes e controle seu estoque',
    duration: '6 min',
    steps: [
      'Acesse "Estoque" no menu',
      'Clique em "Novo Ingrediente"',
      'Preencha nome, unidade (kg, L, un), custo e estoque mínimo',
      'Para fazer entrada, selecione o ingrediente e clique "Entrada"',
      'Informe quantidade recebida e custo unitário',
      'Para vincular a produtos, edite o produto e acesse "Receita"',
      'Adicione os ingredientes com as quantidades por unidade do produto',
    ],
  },
  {
    id: 'printer-setup',
    title: 'Configurando Impressoras',
    description: 'Como configurar impressão para cozinha e caixa',
    duration: '8 min',
    steps: [
      'Baixe e instale o QZ Tray em qz.io',
      'Execute o QZ Tray (ícone aparece na bandeja do sistema)',
      'No sistema, acesse "Impressoras"',
      'Clique em "Testar Conexão QZ Tray"',
      'Após conectado, clique em "Nova Impressora"',
      'Selecione a impressora e defina as funções',
      'Em "Locais de Produção", vincule locais às impressoras',
      'Nos produtos, defina o local de produção de cada um',
    ],
  },
  {
    id: 'nfce-setup',
    title: 'Ativando NFC-e no Restaurante',
    description: 'Como solicitar a ativação da emissão fiscal',
    duration: '5 min',
    steps: [
      'Certifique-se de ter o certificado digital A1 válido',
      'Entre em contato com o suporte técnico do Altus Food',
      'Agende a instalação do ACBrMonitor no computador',
      'O suporte irá configurar todo o ambiente necessário',
      'Após a configuração, teste a emissão na página Fiscal',
      'Emita uma nota de teste para validar a configuração',
    ],
  },
];

const quickTips = [
  { tip: 'Use a busca rápida no PDV digitando o nome do produto', icon: Search },
  { tip: 'Clique duas vezes no produto para adicionar rapidamente', icon: ShoppingCart },
  { tip: 'Mesas com chamada de garçom ficam piscando em vermelho', icon: Grid3X3 },
  { tip: 'O relatório de CMV ajuda a identificar produtos com margem baixa', icon: BarChart3 },
  { tip: 'Configure alertas de WhatsApp para estoque baixo', icon: MessageCircle },
];

export default function AjudaPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFaq = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => 
        q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.a.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter(category => category.questions.length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold">Central de Ajuda</h1>
          <p className="text-muted-foreground mt-1">
            Encontre respostas, tutoriais e guias para usar o sistema
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar na Central de Ajuda..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Quick Tips */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-brand" />
            Dicas Rápidas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {quickTips.map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
              >
                <item.icon className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                <span className="text-sm">{item.tip}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultValue="faq" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Perguntas Frequentes
          </TabsTrigger>
          <TabsTrigger value="tutorials" className="flex items-center gap-2">
            <PlayCircle className="h-4 w-4" />
            Tutoriais
          </TabsTrigger>
        </TabsList>

        {/* FAQ Tab */}
        <TabsContent value="faq" className="space-y-6">
          {searchTerm && filteredFaq.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <HelpCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Nenhum resultado encontrado</p>
                <p className="text-muted-foreground">
                  Tente buscar por outras palavras-chave
                </p>
              </CardContent>
            </Card>
          )}

          {(searchTerm ? filteredFaq : faqCategories).map((category) => (
            <Card key={category.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <category.icon className="h-5 w-5 text-brand" />
                  {category.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((item, index) => (
                    <AccordionItem key={index} value={`${category.id}-${index}`}>
                      <AccordionTrigger className="text-left">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Tutorials Tab */}
        <TabsContent value="tutorials" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {tutorials.map((tutorial) => (
              <Card key={tutorial.id} className="overflow-hidden">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-lg">{tutorial.title}</CardTitle>
                      <CardDescription>{tutorial.description}</CardDescription>
                    </div>
                    <Badge variant="secondary" className="shrink-0">
                      {tutorial.duration}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {tutorial.steps.map((step, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand/10 text-brand text-xs font-medium shrink-0">
                          {index + 1}
                        </span>
                        <span className="text-sm text-muted-foreground">{step}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Contact Support */}
      <Card className="bg-gradient-to-r from-brand/5 to-brand/10 border-brand/20">
        <CardContent className="py-6">
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <div className="p-3 rounded-full bg-brand/10">
              <MessageCircle className="h-8 w-8 text-brand" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Precisa de mais ajuda?</h3>
              <p className="text-muted-foreground text-sm">
                Entre em contato com nosso suporte pelo WhatsApp para atendimento personalizado.
              </p>
            </div>
            <a
              href="https://wa.me/5511999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand text-brand-foreground font-medium hover:opacity-90 transition-opacity"
            >
              <MessageCircle className="h-4 w-4" />
              Falar com Suporte
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
